import { ProgramEngagementModel, ProgramTask } from '@cibo/core'
import { ENGAGEMENT_ROUTES, EngagementRouteParams } from '@cibo/ui'
import { ModalProps } from '@mui/material'
import { propEq } from 'ramda'
import { Dispatch, SetStateAction, useCallback } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { useProgramEngagementContext } from '../../context'

export const useNavigateToNextTask = ({
  setTaskDialogState,
  fallbackDestination,
}: {
  setTaskDialogState?: Dispatch<
    SetStateAction<Pick<ModalProps, 'open'> & Pick<EngagementRouteParams, 'taskId'>>
  >
  fallbackDestination?: string
}) => {
  const navigate = useNavigate()
  const { basePath, program, engagementId, stageId, stepId } = useProgramEngagementContext()

  const navigateNext = useCallback(
    ({
      taskId,
      engagement,
    }: {
      taskId?: ProgramTask['id']
      engagement: ProgramEngagementModel
    }) => {
      const nextStepAndTask = engagement.nextAvailableStepAndTask({ stepId, taskId })
      const stepContent = program.data?.stepContent(stepId)?.content?.status

      const stepStatus = engagement.getStepStatus(stepId)
      const stepHasStatusDisplay = !!stepStatus?.displays?.find(({ type }) => type !== 'menuItem')

      const hasStepContent = stepHasStatusDisplay || !!stepContent

      // @todo: handle end of application
      // adding `!nextStepAndTask?.nextStepId` breaks tests that read "Congratulations"
      // completed step content on, eg., the last task in the test:
      // "PreferredRankPractice Detail & Task Editor: accept high value practice"
      const nextStepIsStatus = nextStepAndTask?.nextStepId !== stepId && hasStepContent && !!taskId

      if (nextStepAndTask?.nextTaskId && !nextStepIsStatus) {
        // show the step if there is one
        const nextTaskContent = program.data?.taskContent(nextStepAndTask.nextTaskId)
        const nextTaskStatus = engagement.getTaskStatus(nextStepAndTask.nextTaskId)

        const nextTaskEditorParams = nextTaskStatus?.displays?.find(
          propEq('type', 'taskEditor')
        )?.params

        if (nextTaskContent?.hidden || nextTaskEditorParams?.modal) {
          // show dialog
          setTaskDialogState &&
            setTaskDialogState({ open: true, taskId: nextStepAndTask.nextTaskId })
        } else {
          //advance to the next task/step
          navigate(
            generatePath(`${basePath}${ENGAGEMENT_ROUTES.TASK}`, {
              engagementId,
              stageId,
              stepId: nextStepAndTask.nextStepId,
              taskId: nextStepAndTask.nextTaskId,
            })
          )
        }
      } else if (nextStepIsStatus) {
        // if step status is present and not currently being displayed, show the step status
        navigate(
          generatePath(`${basePath}${ENGAGEMENT_ROUTES.STEP_STATUS}`, {
            engagementId,
            stageId,
            stepId,
          })
        )
      } else {
        // go to the default location
        !!fallbackDestination && navigate(fallbackDestination)
      }
    },
    [
      program.data,
      stepId,
      basePath,
      engagementId,
      stageId,
      fallbackDestination,
      navigate,
      setTaskDialogState,
    ]
  )

  return navigateNext
}
